<template>
  <BaseModal
    v-model="showModal"
    title="Créditer le portefeuille"
    size="xl"
    :closable="!isSubmitting"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white shadow-lg">
          <BaseIcon name="plus" :size="24" />
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Créditer le portefeuille</h2>
          <p class="text-sm text-gray-500">Ajouter des fonds au compte client</p>
        </div>
      </div>
    </template>

    <form  class="space-y-6">
      <!-- Résumé du portefeuille -->
      <div class="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div class="flex justify-between items-center py-2 border-b border-gray-200">
          <span class="text-sm text-gray-600">Client</span>
          <span class="text-sm font-semibold text-gray-900">{{ customerName }}</span>
        </div>
        <div class="flex justify-between items-center pt-3 mt-2 bg-green-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-xl">
          <span class="text-sm font-medium text-gray-700">Solde actuel</span>
          <span class="text-base font-bold text-green-600">{{ formatCurrency(currentBalance) }}</span>
        </div>
      </div>

      <!-- Détails du crédit -->
      <div>
        <div class="flex items-center gap-2 text-green-600 font-semibold mb-4 pb-2 border-b-2 border-green-200">
          <BaseIcon name="dollar-sign" :size="18" />
          <span>Détails du crédit</span>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Montant à créditer <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                v-model.number="form.amount"
                type="number"
                min="1"
                step="1"
                class="w-full px-4 py-3 pr-16 text-base border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/10 transition-all"
                :class="{ 'border-red-500': validationErrors.amount }"
                placeholder="Entrez le montant"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">FCFA</span>
            </div>
            <span v-if="validationErrors.amount" class="text-xs text-red-500 mt-1 block">{{ validationErrors.amount }}</span>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Description (optionnel)</label>
            <textarea
              v-model="form.description"
              rows="2"
              class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/10 transition-all resize-none"
              placeholder="Raison du crédit, référence..."
            ></textarea>
          </div>

          <!-- Prévisualisation du nouveau solde -->
          <div v-if="form.amount > 0" class="bg-green-50 rounded-lg p-4 border border-green-200">
            <div class="flex justify-between items-center">
              <span class="text-sm text-green-700">Nouveau solde après crédit</span>
              <span class="text-lg font-bold text-green-600">{{ formatCurrency(newBalance) }}</span>
            </div>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <span class="text-xs text-gray-500">* Champs obligatoires</span>
        <div class="flex gap-3">
          <BaseButton variant="ghost" :disabled="isSubmitting" @click="closeModal">
            Annuler
          </BaseButton>
          <BaseButton
            variant="success"
            :loading="isSubmitting"
            :disabled="!isFormValid"
            @click="submitCredit"
          >
            <template #icon-left>
              <BaseIcon name="plus" :size="18" />
            </template>
            Créditer le compte
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import customerService from '@/services/customerService'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseIcon } from '@/components/base'

export default defineComponent({
  name: 'CreditWalletModal',
  components: {
    BaseModal,
    BaseButton,
    BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: [Number, String],
      required: true
    },
    customerName: {
      type: String,
      default: ''
    },
    currentBalance: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'credit-added'],
  setup(props, { emit }) {
    const toastStore = useToastStore()
    const isSubmitting = ref(false)
    const validationErrors = ref({})

    const form = ref({
      amount: null,
      description: ''
    })

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close')
        }
      }
    })

    const newBalance = computed(() => {
      return (props.currentBalance || 0) + (form.value.amount || 0)
    })

    const isFormValid = computed(() => {
      return form.value.amount > 0
    })

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const resetForm = () => {
      form.value = {
        amount: null,
        description: ''
      }
      validationErrors.value = {}
    }

    const validateForm = () => {
      validationErrors.value = {}
      let isValid = true

      if (!form.value.amount || form.value.amount <= 0) {
        validationErrors.value.amount = 'Le montant doit être supérieur à 0'
        isValid = false
      }

      return isValid
    }

    const submitCredit = async () => {
      if (isSubmitting.value) return
      if (!validateForm()) return

      try {
        isSubmitting.value = true
        await customerService.creditCustomerWallet(props.customerId, {
          amount: form.value.amount,
          description: form.value.description || undefined
        })

        toastStore.addToast({
          type: 'success',
          message: 'Portefeuille crédité avec succès',
          duration: 4000
        })

        emit('credit-added')
        closeModal()
      } catch (error) {
        console.error('Erreur:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du crédit du portefeuille',
          duration: 4000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        resetForm()
      }
    })

    return {
      showModal,
      form,
      isSubmitting,
      validationErrors,
      newBalance,
      isFormValid,
      formatCurrency,
      submitCredit,
      closeModal
    }
  }
})
</script>
